import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, testimonials } from '../mock';
import SEO from '../components/SEO';

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Show 3 cards on desktop, 1 on mobile
  const getVisibleCount = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 768) return 2;
    }
    return 1;
  };

  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => setVisibleCount(getVisibleCount());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  const goNext = useCallback(() => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  // Auto-rotate every 4 seconds
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(goNext, 4000);
    return () => clearInterval(timer);
  }, [isPaused, goNext]);

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + visibleCount);
  // Handle wrap-around if needed
  if (visibleTestimonials.length < visibleCount) {
    const remaining = visibleCount - visibleTestimonials.length;
    visibleTestimonials.push(...testimonials.slice(0, remaining));
  }

  return (
    <section className="py-16 bg-white" data-testid="testimonial-carousel">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">What Our Clients Say</h2>
          <p className="text-lg text-slate-600">Real experiences from satisfied customers</p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            className="absolute -left-2 lg:-left-5 top-1/2 -translate-y-1/2 z-10 bg-white border border-slate-200 rounded-full p-2 shadow-md hover:shadow-lg hover:bg-sky-50 transition-all"
            aria-label="Previous testimonial"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <button
            onClick={goNext}
            className="absolute -right-2 lg:-right-5 top-1/2 -translate-y-1/2 z-10 bg-white border border-slate-200 rounded-full p-2 shadow-md hover:shadow-lg hover:bg-sky-50 transition-all"
            aria-label="Next testimonial"
            data-testid="carousel-next"
          >
            <ChevronRight className="h-5 w-5 text-slate-600" />
          </button>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-8">
            {visibleTestimonials.map((testimonial) => (
              <Card key={`${testimonial.id}-${currentIndex}`} className="border-sky-100 hover:shadow-lg transition-all duration-500 animate-fadeIn">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-gradient-to-br from-sky-100 to-emerald-100 w-12 h-12 rounded-full flex items-center justify-center text-sky-700 font-bold text-lg flex-shrink-0">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">{testimonial.name}</h4>
                      <p className="text-sm text-slate-500">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className={`h-4 w-4 ${star <= testimonial.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                    ))}
                  </div>
                  <p className="text-slate-600 italic leading-relaxed">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-2 mt-8" data-testid="carousel-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                i === currentIndex ? 'w-8 bg-sky-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={`Go to testimonial group ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
            />
          ))}
        </div>

        {/* Link to all reviews */}
        <div className="text-center mt-6">
          <Link to="/reviews" className="text-sky-600 hover:text-sky-700 font-medium text-sm transition-colors">
            Read all customer reviews &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div>
      <SEO
        title="Professional Cleaning Services in London"
        description="Serene Sparkle offers professional residential, commercial, and specialty cleaning services in London. Trusted cleaners, competitive prices. Get a free quote today!"
        keywords="cleaning service London, house cleaning, office cleaning, deep cleaning, end of tenancy cleaning, professional cleaners London"
        canonical="/"
      />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Serene Sparkle",
          "description": "Professional cleaning services in London. Residential, commercial, and specialty cleaning.",
          "url": "https://serene-world.com",
          "telephone": "07951488263",
          "email": "Team@serene-world.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "addressCountry": "GB"
          },
          "priceRange": "£18-£500",
          "openingHoursSpecification": [
            { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "19:00" },
            { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "13:00" }
          ]
        })}
      </script>
      {/* Hero Section */}
      <section
        className="relative h-[600px] flex items-start pt-20 justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.85), rgba(224, 242, 254, 0.9)), url('https://images.unsplash.com/photo-1648475236583-2e25a6cbf3bd?w=1200&q=70&auto=format')`,
          backgroundBlendMode: 'overlay'
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-6 shadow-sm">
            <Sparkles className="h-4 w-4 text-sky-600" />
            <span className="text-sm font-medium text-slate-700">Professional Cleaning Services</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Clean Space,<br />
            <span className="text-sky-600">Calm Mind</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Experience exceptional cleaning services that create spaces where you feel safe, comfortable, and at peace.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/contact">
              <Button size="lg" className="bg-sky-600 hover:bg-sky-700 text-white shadow-lg hover:shadow-xl transition-all">
                Request a Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <div className="sm:ml-8">
              <Link to="/services">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all">
                  View Services <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-sky-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="bg-gradient-to-br from-sky-100 to-emerald-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="h-6 w-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">Reliable & Professional</h3>
                <p className="text-slate-600">
                  All team members are fully insured, background-checked, and professionally trained.
                </p>
              </CardContent>
            </Card>
            <Card className="border-sky-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="bg-gradient-to-br from-sky-100 to-emerald-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">Eco-Friendly Products</h3>
                <p className="text-slate-600">
                  We use safe, environmentally friendly cleaning products that are gentle yet effective.
                </p>
              </CardContent>
            </Card>
            <Card className="border-sky-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="bg-gradient-to-br from-sky-100 to-emerald-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Star className="h-6 w-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">Satisfaction Guaranteed</h3>
                <p className="text-slate-600">
                  Not satisfied? We'll make it right within 24 hours at no extra charge.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive cleaning solutions tailored to your needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="overflow-hidden hover:shadow-xl transition-shadow border-sky-100">
                <div className="h-48 overflow-hidden">
                  <img
                    src={`${service.image}?w=600&q=75&auto=format`}
                    alt={`${service.title} - Professional cleaning by Serene Sparkle`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    width="600"
                    height="400"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-semibold text-slate-800 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  <Link to={`/services/${service.id}`}>
                    <Button variant="outline" className="w-full border-sky-600 text-sky-600 hover:bg-sky-50">
                      View {service.title} Details <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <TestimonialCarousel />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-sky-100 to-emerald-100">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Ready for a Spotless Space?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote tailored to your cleaning needs. We respond within 24-48 hours.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-sky-600 hover:bg-sky-700 text-white shadow-lg hover:shadow-xl transition-all">
              Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Admin Access Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <Card className="border-emerald-200 hover:shadow-xl transition-shadow cursor-pointer">
              <Link to="/admin">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="bg-gradient-to-br from-emerald-100 to-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">🔐</span>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Admin Dashboard</h3>
                    <p className="text-slate-600 mb-4">
                      Manage quotes, approve requests, and generate payment links
                    </p>
                    <Button className="bg-emerald-600 hover:bg-emerald-700 w-full">
                      Access Admin Dashboard
                    </Button>
                  </div>
                </CardContent>
              </Link>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
